<template>
  <header>
    <section v-if="!loading" class="view-selected-project-header">
      <div :class="[$vuetify.breakpoint.smAndUp ? 'd-flex' : '']">
        <!-- Project Logo -->
        <div
          @click="$emit('logo-clicked')"
          class="cursor-pointer"
          :class="[$vuetify.breakpoint.smAndUp ? 'mr-4' : '']"
        >
          <base-image
            v-if="logo"
            type="p"
            image-type="n"
            :contain="false"
            :project-id="$route.params.id"
            :file-name="logo"
            :image-props="{ contain: true }"
            :height="logoHeightAndWidth"
            :width="logoHeightAndWidth"
            :aspectRatio="1"
            style="border-radius: 5px;"
            :imageAlt="`${$t('imageForProject')} ${project.project.name}`"
          >
          </base-image>
          <div
            v-else-if="$vuetify.breakpoint.smAndUp"
            class="d-flex justify-center align-center"
            :style="noLogoStyle"
          >
            {{ project.project.name[0] }}
          </div>
        </div>

        <!-- Project Socials and Names -->
        <div>
          <component
            :is="
              $vuetify.breakpoint.mdAndUp
                ? 'h1'
                : $vuetify.breakpoint.sm
                ? 'h2'
                : 'h3'
            "
            class="ma-0 pa-0"
            >{{ projectName }}</component
          >

          <p class="ma-0 pa-0 mb-2 leader-style" v-if="projectLeaderName">
            {{
              $route.params.groupId ? $t('groupLeader') : $t('projectLeader')
            }}
            {{ projectLeaderName }}
          </p>

          <!-- Member and Document Icons and Count -->
          <div class="d-flex">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-flex align-center mr-2">
                  <div class="mr-1">
                    <v-img
                      width="20px"
                      :src="icons.showcaseIcon"
                      :alt="$t('showcaseCountIcon')"
                    ></v-img>
                  </div>
                  <span class="primary--text text--lighten-2">{{
                    documentCount
                  }}</span>
                </div>
              </template>
              <span>{{
                `${$t('showcasesUpperCase')} & ${$t('creativities')}`
              }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-flex align-center">
                  <div class="mr-1">
                    <v-img
                      width="24px"
                      :src="icons.peopleIcon"
                      :alt="$t('memberCountIcon')"
                    ></v-img>
                  </div>
                  <span
                    class="primary--text text--lighten-2"
                    :alt="$t('memberCountIcon')"
                    >{{ memberCount }}</span
                  >
                </div>
              </template>
              <span>{{ $t('members') }}</span>
            </v-tooltip>
          </div>

          <!-- Share menu, email and report -->
          <div class="d-flex align-center mt-2">
            <div class="d-flex mr-2">
              <share-menu
                @close="showProjectShare = false"
                :entity="entity"
                class="mr-2"
                :show="showProjectShare"
                :entity-id="entityId"
                :title="title"
                :key="`share-menu-${updatedKey}`"
                :projectHashtags="projectHashtags"
              >
                <template v-slot:activator>
                  <v-btn depressed fab x-small @click="showProjectShare = true">
                    <v-icon class="primary--text" style="font-size: 20px;">
                      {{ icons.mdiShareVariant }}
                    </v-icon>
                  </v-btn>
                </template>
              </share-menu>

              <v-btn
                v-if="emailInfo"
                class="mr-2"
                :href="`mailto:${emailInfo}`"
                x-small
                fab
                depressed
              >
                <v-icon color="primary">{{ icons.mdiEmail }}</v-icon>
              </v-btn>

              <v-btn
                v-if="website"
                class="mr-2"
                :href="website"
                target="_blank"
                x-small
                fab
                depressed
              >
                <v-icon :size="22" color="primary">{{ icons.mdiWeb }}</v-icon>
              </v-btn>

              <!-- Menu Report Abuse -->
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn v-on.stop="on" x-small fab depressed>
                    <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-if="
                      userPermissions.includes('PROJECT_GROUP_EDIT') ||
                        userPermissions.includes('PROJECT_EDIT')
                    "
                    @click="goToSettings"
                  >
                    <v-list-item-content>
                      <v-row class="ma-0" align="center">
                        <v-icon color="primary" class="mr-2">{{
                          icons.mdiPencil
                        }}</v-icon>
                        {{ $t('editProject') }}
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item
                    v-if="
                      userPermissions.includes('PROJECT_GROUP_INVITE') &&
                        !isArchived
                    "
                    @click="
                      maxedOutProjectMembers
                        ? $emit('show-pricing-dialog', 'invite')
                        : $emit('invite-member')
                    "
                  >
                    <v-list-item-content>
                      <v-row class="ma-0" align="center">
                        <v-icon color="primary" class="mr-2">{{
                          icons.mdiAccountMultiplePlusOutline
                        }}</v-icon>
                        {{ $t('inviteMembers') }}
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item @click.stop="showReportAbuse = true">
                    <v-list-item-content>
                      <v-row class="ma-0" align="center">
                        <v-icon color="primary" class="mr-2">{{
                          icons.mdiAlertOctagon
                        }}</v-icon>
                        {{ $t('reportAbuse') }}
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <div v-if="hideJoin">
              <v-btn
                @click="$emit('join')"
                small
                color="brand"
                class="white--text"
                depressed
                >{{ $t('joinProject') }}</v-btn
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Nav bar options -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <div class="d-flex align-center">
          <!-- Notifications -->
          <template v-if="isLoggedIn">
            <v-menu bottom left offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on }">
                <div style="position: relative; width: 45px" class="mr-3">
                  <v-btn
                    v-on="on"
                    @click="fetchNotifications"
                    fab
                    small
                    depressed
                    text
                  >
                    <v-icon>
                      {{ icons.mdiBellOutline }}
                    </v-icon>
                  </v-btn>
                  <div
                    v-if="notificationCount > 0"
                    class="d-flex justify-center align-center pa-0 ma-0"
                    style="position: absolute; font-size: 9px; top: 0; right: 0; border-radius: 50%; width: 18px; height: 18px; background: red; color: white"
                  >
                    <b v-if="notificationCount < 100">{{
                      notificationCount
                    }}</b>
                    <b v-else>99+</b>
                  </div>
                </div>
              </template>

              <div style="max-height: 350px">
                <v-list style="min-width: 300px;">
                  <v-list-item v-if="notificationError">
                    <v-list-item-content>
                      {{ $t('notificationErrorMsg') }}
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-else-if="notificationLoader">
                    <v-list-item-content>
                      <v-icon class="anim-rotate">{{
                        icons.mdiAutorenew
                      }}</v-icon>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-else-if="notifications.length <= 0">
                    <v-list-item-content>
                      {{ $t('youHaveNoNewNotifications') }}
                    </v-list-item-content>
                  </v-list-item>
                  <notification-list-item
                    v-else
                    v-for="(notification, i) in notifications"
                    @markedAsReadCounter="count => (notificationCount += count)"
                    @delete-notification="
                      deleteNotificationHandler(notification)
                    "
                    @show-invite-details="showInviteDetails(notification)"
                    @show-role-request-details="
                      showRoleRequestDetails(notification)
                    "
                    :key="i"
                    :id="notification.notice.noticeId.replace('#', '')"
                    :notification="notification"
                    :deleteLoading="deleteLoading"
                    class="list-item-hover"
                  ></notification-list-item>
                  <v-list-item v-if="doneFetchingNotifications">
                    <v-list-item-content>
                      <v-icon
                        v-intersect="loadMoreNotifications"
                        class="anim-rotate"
                        >{{ icons.mdiAutorenew }}</v-icon
                      >
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-menu>
          </template>

          <!-- Language Selection -->
          <v-menu bottom left offset-y>
            <template v-slot:activator="{ on }">
              <img
                v-on="on"
                class="cursor-pointer mr-3"
                :src="chosenFlag"
                style="height: 25px; width:45px;"
                :alt="`${$t('activeLanguage')}`"
              />
            </template>

            <v-list>
              <v-list-item
                v-for="lang in filteredLanguages"
                :key="lang.text"
                @click="setDefaultLanguage(lang.value), setLanguage(lang.value)"
              >
                <v-img
                  :src="lang.flag"
                  height="45"
                  width="45"
                  aspect-ratio="1"
                  contain
                  :alt="lang.text"
                ></v-img>

                <v-list-item-title class="ml-2">
                  {{ lang.text }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <template v-if="isLoggedIn">
            <!-- Create Menu -->
            <v-menu open-on-hover bottom offset-y class="mr-3">
              <template v-slot:activator="{ on }">
                <v-btn text v-on="on" class="pl-0" id="create-list">
                  {{ $t('create') }}
                  <v-icon right dark color="primary">{{
                    icons.mdiChevronDown
                  }}</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item v-if="canCreateProject" to="/create-project">
                  <v-list-item-title>{{ $t('newProject') }}</v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-else
                  @click="$emit('show-pricing-dialog')"
                  class="inputBg"
                >
                  <v-list-item-title>
                    <v-row no-gutters align="center">
                      <span class="mr-3">{{ $t('newProject') }}</span>
                      <v-chip
                        v-if="brand.subscription.type.toLowerCase() !== 'paid'"
                        x-small
                        color="#febc0a"
                        label
                      >
                        {{ $t('pro') }}
                      </v-chip>

                      <div
                        v-else
                        class="d-flex align-center justify-center pa-1"
                        :style="[
                          {
                            border: `1px solid ${$vuetify.theme.themes.light.warning}`,
                            borderRadius: '50%'
                          }
                        ]"
                      >
                        <v-icon small color="warning">{{
                          icons.mdiLockAlertOutline
                        }}</v-icon>
                      </div>
                    </v-row>
                  </v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-if="canCreateDocuments"
                  @click="goToCreateShowcase()"
                >
                  <v-list-item-title class="mr-3">{{
                    $t('newShowcase')
                  }}</v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-else
                  @click="$emit('show-pricing-dialog')"
                  class="inputBg"
                  name="create-document-item-non-pro"
                >
                  <v-list-item-title>
                    <v-row no-gutters align="center">
                      <span class="mr-3">{{ $t('newShowcase') }}</span>
                      <v-chip
                        v-if="brand.subscription.type.toLowerCase() !== 'paid'"
                        x-small
                        color="#febc0a"
                        label
                      >
                        {{ $t('pro') }}
                      </v-chip>

                      <div
                        v-else
                        class="d-flex align-center justify-center pa-1"
                        :style="[
                          {
                            border: `1px solid ${$vuetify.theme.themes.light.warning}`,
                            borderRadius: '50%'
                          }
                        ]"
                      >
                        <v-icon small color="warning">{{
                          icons.mdiLockAlertOutline
                        }}</v-icon>
                      </div>
                    </v-row>
                  </v-list-item-title>
                </v-list-item>

                <v-list-item v-if="canCreateTemplate" to="/create-template">
                  <v-list-item-title>{{ $t('newTemplate') }}</v-list-item-title>
                </v-list-item>

                <v-list-item v-else @click="$emit('show-pricing-dialog')">
                  <v-list-item-title>
                    <v-row no-gutters align="center">
                      <span class="mr-3">{{ $t('newTemplate') }}</span>
                      <v-chip x-small color="#febc0a" label>
                        {{ $t('pro') }}
                      </v-chip>
                    </v-row>
                  </v-list-item-title>
                </v-list-item>

                <v-list-item
                  @click="
                    !canCreateItinerary
                      ? $emit('show-pricing-dialog')
                      : $router.push('/itinerary/builder')
                  "
                >
                  <v-list-item-title>
                    <v-row no-gutters align="center">
                      <span class="mr-3">{{ $t('newItinerary') }}</span>

                      <template v-if="!canCreateItinerary">
                        <v-chip
                          v-if="
                            brand.subscription.type.toLowerCase() !== 'paid'
                          "
                          x-small
                          color="#febc0a"
                          label
                        >
                          {{ $t('pro') }}
                        </v-chip>

                        <div
                          v-else
                          class="d-flex align-center justify-center pa-1"
                          :style="[
                            {
                              border: `1px solid ${$vuetify.theme.themes.light.warning}`,
                              borderRadius: '50%'
                            }
                          ]"
                        >
                          <v-icon small color="warning">{{
                            icons.mdiLockAlertOutline
                          }}</v-icon>
                        </div>
                      </template>
                    </v-row>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- Profile Menu -->
            <v-menu bottom left offset-y>
              <template v-slot:activator="{ on }">
                <div
                  v-if="impersonating"
                  class="d-flex cursor-pointer align-center"
                  v-on="on"
                >
                  <v-badge color="brand" icon="mdi-mdiAccountQuestion">
                    <template v-slot:badge>
                      <v-icon x-small>{{ icons.mdiAccountQuestion }}</v-icon>
                    </template>

                    <v-avatar
                      v-on="on"
                      size="45px"
                      class="cursor-pointer"
                      id="profile-list"
                    >
                      <base-image
                        type="u"
                        image-type="t"
                        :profile-id="
                          userProfile.profile.profileId.replace('#', '')
                        "
                        :file-name="userProfile.profile.logo"
                        :contain="false"
                        :isProfileImage="true"
                        :imageAlt="
                          `${$t('impersonatingUser')} ${
                            userProfile.profile.name
                          }`
                        "
                      ></base-image>
                    </v-avatar>
                  </v-badge>
                  <v-icon color="primary" small>{{
                    icons.mdiChevronDown
                  }}</v-icon>
                </div>

                <div
                  v-else
                  class="d-flex cursor-pointer align-center"
                  v-on="on"
                >
                  <v-avatar id="profile-list" size="45px">
                    <base-image
                      type="u"
                      image-type="t"
                      :profile-id="
                        userProfile.profile.profileId.replace('#', '')
                      "
                      :file-name="userProfile.profile.logo"
                      :isProfileImage="true"
                      :imageAlt="
                        `${$t('profileImage')} ${userProfile.profile.name}`
                      "
                    ></base-image>
                  </v-avatar>
                  <v-icon color="primary" small>{{
                    icons.mdiChevronDown
                  }}</v-icon>
                </div>
              </template>

              <v-list>
                <v-list-item>
                  <v-list-item-title>{{
                    userProfile.profile.name
                  }}</v-list-item-title>
                </v-list-item>

                <v-divider></v-divider>

                <v-list-item
                  :to="
                    `/profile/${userProfile.profile.profileId.replace(
                      '#',
                      ''
                    )}/${defaultLanguage}`
                  "
                >
                  <v-list-item-title>{{
                    impersonating ? $t('profile') : $t('myProfile')
                  }}</v-list-item-title>
                </v-list-item>

                <v-list-item @click="showReportForm = true">
                  <v-list-item-title>{{
                    $t('provideFeedback')
                  }}</v-list-item-title>
                </v-list-item>

                <v-list-item to="/about-us">
                  <v-list-item-title>{{ $t('settings') }}</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="isAdmin" to="/platform-settings">
                  <v-list-item-title>{{
                    $t('platformSettings')
                  }}</v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-if="impersonating"
                  @click="stopImpersonatingHandler()"
                >
                  <v-list-item-title>{{
                    $t('stopImpersonating')
                  }}</v-list-item-title>
                </v-list-item>

                <v-list-item @click="logoutHandler">
                  <v-list-item-title>{{ $t('logout') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <!-- Login Btn -->
          <template v-else>
            <v-btn
              class="white--text"
              color="brand"
              elevation="0"
              to="/login"
              >{{ $t('login') }}</v-btn
            >
          </template>
        </div>
      </div>
    </section>

    <!-- Loader Section -->
    <section v-else>
      <v-skeleton-loader
        type="image"
        width="130px"
        height="130px"
      ></v-skeleton-loader>
      <v-skeleton-loader
        class="mt-2"
        type="heading"
        width="100%"
      ></v-skeleton-loader>
      <v-skeleton-loader
        class="mt-2"
        type="text"
        width="50%"
      ></v-skeleton-loader>
    </section>

    <base-report-abuse
      v-if="showReportAbuse"
      :show="showReportAbuse"
      entityType="PROJECT"
      :entityName="project.project.name"
      :entityId="project.project.projectId"
      @close="showReportAbuse = false"
    ></base-report-abuse>

    <invite-dialog-details
      v-if="showInviteDetailsDialog"
      :show="showInviteDetailsDialog"
      :invitedBy="invitedBy"
      :invitationDetails="invitationDetails"
      :loading="inviteActionLoading"
      @close="showInviteDetailsDialog = false"
      @invite-aciton="invitationActionHandler"
    ></invite-dialog-details>

    <base-message-dialog
      v-if="displayMessageDialog"
      :show="displayMessageDialog"
    >
      <template v-slot:title>
        <v-row
          v-if="messageType == 'roleRequest'"
          align="center"
          class="ma-0 mb-4"
        >
          <v-avatar size="32px" class="mr-2">
            <base-image
              type="u"
              image-type="t"
              :profile-id="notificationOrigin.id"
              :file-name="notificationOrigin.logo"
              :isProfileImage="true"
              :imageAlt="`${$t('notificationLogo')}`"
            ></base-image>
          </v-avatar>

          <h3>{{ notificationOrigin.name }} {{ $t('requestedNewRole') }}</h3>
        </v-row>

        <h4 class="mt-2">{{ messageTittle }}</h4>
      </template>

      <template v-slot:message>
        <p class="mt-2">
          {{ messageDescription }}
        </p>
      </template>

      <template v-slot:card-action>
        <v-btn @click="closeMessageDialogHandler()" text class="mr-2">{{
          $t('understood')
        }}</v-btn>

        <v-btn
          v-if="messageType == 'invitationNotFound'"
          @click="
            closeMessageDialogHandler(),
              $router.push(
                `/view-projects/${inviteProjectId}/${defaultLanguage}`
              )
          "
          color="brand"
          class="white--text"
          >{{ $t('visitProject') }}</v-btn
        >
      </template>
    </base-message-dialog>

    <base-bug-improvement-form
      v-if="showReportForm"
      :show="showReportForm"
      @close="showReportForm = false"
    >
    </base-bug-improvement-form>
  </header>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import {
  mdiChevronDown,
  mdiDotsVertical,
  mdiAlertOctagon,
  mdiAccountMultiplePlusOutline,
  mdiBellOutline,
  mdiAutorenew,
  mdiAccountQuestion,
  mdiPencil,
  mdiShareVariant,
  mdiEmail,
  mdiWeb,
  mdiLockAlertOutline
} from '@mdi/js';

import Cookies from 'js-cookie';
import Swal from 'sweetalert2';

import { profiles as profileAPI, notices, membersOf } from '@/utilities/api';

// import websiteIcon from '@/assets/icons/website.svg';
import showcaseIcon from '@/assets/icons/showcases.svg';
import peopleIcon from '@/assets/icons/people.svg';

import ShareMenu from '@/components/base_components/ShareMenu/ShareMenu.vue';
import BaseImage from '@/components/base_components/BaseImage/BaseImage.vue';
import NotificationListItem from '@/components/NotificationListItem.vue';
const BaseReportAbuse = () =>
  import('@/components/base_components/BaseReportAbuse/BaseReportAbuse.vue');
const InviteDialogDetails = () =>
  import('@/components/InviteDialogDetails.vue');
const BaseMessageDialog = () =>
  import(
    '@/components/base_components/BaseMessageDialog/BaseMessageDialog.vue'
  );
const BaseBugImprovementForm = () =>
  import(
    '@/components/base_components/BaseBugImprovementForm/BaseBugImprovementForm.vue'
  );

export default {
  name: 'ViewSelectedProjectHeader',
  components: {
    BaseImage,
    ShareMenu,
    BaseReportAbuse,
    InviteDialogDetails,
    BaseMessageDialog,
    NotificationListItem,
    BaseBugImprovementForm
  },

  props: {
    loading: {
      type: Boolean,
      default: () => false
    },
    project: {
      type: Object,
      default: () => ({})
    },
    documentCount: {
      type: Number,
      default: () => 0
    },
    memberCount: {
      type: Number,
      default: () => 0
    },
    userPermissions: {
      type: Array,
      default: () => []
    },
    updatedKey: {
      type: Number,
      default: 0
    },
    maxedOutProjectMembers: {
      type: Boolean,
      required: false,
      default: () => false
    },
    isArchived: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    showProjectShare: false,
    hideJoin: true,
    showReportAbuse: false,
    notificationLoader: false,
    notificationPayload: {
      page: 1,
      pageSize: 10,
      sort: 'notice.creationDate',
      order: 'desc'
    },
    notifications: [],
    notificationCount: 0,
    doneFetchingNotifications: false,
    notificationError: false,
    deleteLoading: true,
    notificationForInvite: {},
    inviteToken: '',
    invitedBy: {},
    invitationDetails: {},
    messageType: '',
    messageTittle: '',
    messageDescription: '',
    displayMessageDialog: false,
    showInviteDetailsDialog: false,
    impersonating: false,
    showReportForm: false
  }),

  async created() {
    if (this.userProfile?.profile) {
      const userProjects = await profileAPI.getProjectGroups(
        this.userProfile?.profile?.profileId.replace('#', '')
      );
      const projectIds = userProjects.list.map(({ project }) =>
        project.projectId.replace('#', '')
      );

      this.hideJoin = projectIds.indexOf(this.$route.params.id) < 0;

      this.getNotificationCount();
    }
  },

  mounted() {
    this.checkIfImpersonating();
  },

  updated() {
    this.getNotificationCount();
    this.checkIfImpersonating();
  },

  methods: {
    ...mapActions([
      'setDefaultLanguage',
      'openNavDrawer',
      'logout',
      'getTranslatedAppLabels',
      'getUserProfile',
      'resetAllState'
    ]),

    async fetchNotifications() {
      try {
        this.notificationLoader = true;
        this.notificationPayload.page = 1;
        const res = await notices.get(this.notificationPayload);
        this.notifications = res.list;
        this.doneFetchingNotifications = res.hasNext;
        this.notificationLoader = false;
        this.notificationError = false;
      } catch (e) {
        this.notificationLoader = false;
        this.notificationError = true;
      }
    },

    async loadMoreNotifications() {
      this.notificationPayload.page++;
      const res = await notices.get(this.notificationPayload);
      this.notifications = [...this.notifications, ...res.list];
      this.doneFetchingNotifications = res.hasNext;
    },

    async getNotificationCount() {
      if (!this.isLoggedIn) return;

      const notificationCount = await notices.getCount({ markAsRead: false });
      this.notificationCount = notificationCount.count;
    },

    showRoleRequestDetails(notification) {
      const { message, role } = notification.notice.data[0].suggestion;

      this.notificationForInvite = notification;
      this.messageType = 'roleRequest';
      this.messageTittle = `${this.$t('roleName')}: ${role}`;
      this.messageDescription = message;

      this.displayMessageDialog = true;
    },

    async deleteNotificationHandler(notification) {
      this.deleteLoading = true;
      const id = notification.notice.noticeId.replace('#', '');

      await notices.delete(id);

      const listItem = document.getElementById(id);
      listItem.classList.add('notice-delete');
      setTimeout(() => {
        listItem.remove();
      }, 500);

      this.notificationCount -= 1;

      this.deleteLoading = false;
    },

    async showInviteDetails(notification) {
      const { token } = notification.notice.data[0];
      const { logo, name, profileId } = notification.origin;
      this.notificationForInvite = notification;
      this.inviteToken = token;
      this.invitedBy = { logo, name, profileId: profileId.replace('#', '') };

      await this.getInviteInfo(token);

      this.showInviteDetailsDialog = true;
    },

    async getInviteInfo(token) {
      try {
        const data = await membersOf.getByToken(token);

        const { project, projectGroup, memberOf } = data;

        this.invitationDetails = { project, projectGroup, memberOf };
      } catch {
        this.messageType = 'invitationNotFound';
        this.messageTittle = this.$t('invitationNoLongerValid');
        this.messageDescription = this.$t('invitationNotValidDesc');
        this.displayMessageDialog = true;
      }
    },

    setLanguage(language) {
      localStorage.setItem('langOption', language);

      const payload = {
        code: language
      };

      this.getTranslatedAppLabels(payload);
    },

    goToSettings() {
      this.$router.push({
        query: {
          section: 'settings'
        }
      });
    },

    goToCreateShowcase() {
      this.$router.push({ name: 'createShowcase' });
    },

    async stopImpersonatingHandler() {
      await Cookies.remove('ksBearerToken');
      await Cookies.remove('ksName');
      await store.dispatch('getUserProfile');

      this.impersonating = false;
      this.$router.push('/');
    },

    logoutHandler() {
      this.logout().then(async res => {
        if (res.status !== 200)
          Swal.fire({
            title: 'Error!',
            text: this.$t('problemLoginOut'),
            type: 'error',
            showConfirmButton: false,
            toast: true,
            position: 'top-end',
            timer: 3000
          });
        else {
          await this.resetAllState();
          this.$router.push('/login');
        }
      });
    },

    checkIfImpersonating() {
      const ksToken = Cookies.get('ksBearerToken');

      if (ksToken !== undefined) {
        this.impersonating = true;
        this.$emit('impersonating');
      }
    },

    async closeMessageDialogHandler() {
      this.messageDescription = '';
      this.messageTittle = '';
      this.displayMessageDialog = false;

      //Delete notification if old invitation
      if (this.messageType == 'invitationNotFound') {
        await this.deleteNotification(this.notificationForInvite);
      }
      this.messageType = '';
      this.notificationForInvite = {};
    }
  },

  computed: {
    ...mapState({
      defaultLanguage: state => state.defaultLanguage,
      userProfile: state => state.authentication.userProfile,
      subscriptions: state => state.authentication.subscriptions,
      filteredLanguages: state => state.languages.filteredLanguages,
      brand: state => state.brand.brand
    }),

    isAdmin() {
      if (!this.userProfile?.profile?.role) return false;
      const { role } = this.userProfile.profile;

      return role.toLowerCase() === 'admin';
    },

    canCreateTemplate() {
      if (!this.brand?.canCreateTemplate) {
        return this.userProfile?.subscription?.name !== 'STANDARD';
      }
      return true;
    },

    canCreateProject() {
      if (!this.userProfile?.profile) return false;
      const { canCreateProject, hasReachedCapacityLimit } = this.subscriptions;

      return canCreateProject && !hasReachedCapacityLimit;
    },

    canCreateItinerary() {
      if (!this.userProfile?.profile) return false;

      const {
        canCreateItinerary,
        canCreateItinerariesOtherProjects
      } = this.subscriptions;

      return canCreateItinerary || canCreateItinerariesOtherProjects;
    },

    canCreateDocuments() {
      const {
        hasReachedCapacityLimit,
        canCreateDocumentsOtherProjects
      } = this.subscriptions;

      return !hasReachedCapacityLimit || canCreateDocumentsOtherProjects;
    },

    chosenFlag() {
      return this.filteredLanguages.find(
        lang => lang.value === this.defaultLanguage
      ).flag;
    },

    isLoggedIn() {
      if (!this.userProfile?.profile) return false;

      return this.userProfile?.profile?.profileId ? true : false;
    },

    hasPermissions() {
      if (this.$route.params.groupId) {
        return (
          this.userPermissions.includes('PROJECT_GROUP_EDIT') ||
          this.userPermissions.includes('PROJECT_GROUP_INVITE')
        );
      }
      return (
        this.userPermissions.includes('PROJECT_GROUP_EDIT') ||
        this.userPermissions.includes('PROJECT_EDIT') ||
        this.userPermissions.includes('PROJECT_GROUP_INVITE')
      );
    },
    icons: () => ({
      mdiChevronDown,
      // websiteIcon,
      showcaseIcon,
      peopleIcon,
      mdiDotsVertical,
      mdiAlertOctagon,
      mdiAccountMultiplePlusOutline,
      mdiBellOutline,
      mdiAutorenew,
      mdiAccountQuestion,
      mdiPencil,
      mdiShareVariant,
      mdiEmail,
      mdiWeb,
      mdiLockAlertOutline
    }),

    logoHeightAndWidth() {
      return this.$vuetify.breakpoint.xs ? '100%' : '130';
    },

    noLogoStyle() {
      return `
      height: ${this.logoHeightAndWidth}px;
        width: ${this.logoHeightAndWidth}px;
        background: ${this?.project?.project?.colors?.bg};
        color: ${this?.project?.project?.colors?.text};
        border-radius: 5px; font-size: 70px`;
    },

    emailInfo() {
      return this.project?.projectLeaderProfile?.template?.[0]?.fields[7]
        ?.value?.[this.defaultLanguage];
    },

    website() {
      let website;
      if (this.$route.params.groupId) {
        website = this.project.projectGroup.website;
      } else {
        website = this.project.project.website;
      }

      if (!website) return '';

      if (website.includes('http')) return website;
      return `http://${website}`;
    },

    logo() {
      if (this.$route.params.groupId) {
        return this.project.projectGroup.logo;
      }
      return this.project?.project?.logo;
    },

    projectName() {
      if (this.$route.params.groupId) {
        return this.project.projectGroup.name;
      }
      return this.project.project.name;
    },

    projectLeaderName() {
      if (this.$route.params.groupId) {
        return this.project.projectGroupLeaderProfile.name;
      }
      return this.project.projectLeaderProfile.name;
    },

    entity() {
      if (this.$route?.params?.groupId) return 'PROJECT_GROUP';
      return 'PROJECT';
    },

    entityId() {
      if (this.$route?.params?.groupId)
        return `${this.$route.params.groupId}-${this.$route.params.id}`;
      return this.$route.params.id;
    },

    title() {
      if (this.$route?.params?.groupId) return this.project.projectGroup.name;
      return this.project.project.name;
    },

    projectHashtags() {
      return this.project?.project?.hashtags
        ? this.project?.project?.hashtags
        : [];
    }
  }
};
</script>

<style scoped>
.leader-style {
  color: rgba(52, 73, 94, 0.35);
  font-size: 15px;
}

.first-join-project {
  display: block;
}

.second-join-project {
  margin-left: auto;
  display: none;
}

@media only screen and (min-width: 600px) {
  .view-selected-project-header {
    display: flex;
    justify-content: space-between;
  }

  .first-join-project {
    display: none;
  }

  .second-join-project {
    display: block;
  }
}

@media only screen and (min-width: 1200px) {
  .view-selected-project-header {
    display: flex;
    justify-content: space-between;
  }
}
</style>
